import { fitWithin } from './fitWithin.js';

export interface FlattenImageOptions {
  /**
   * The mime type of the output image, e.g. `image/jpeg`
   */
  type: string;
  /**
   * The encoding quality (0 to 1) for lossy formats
   */
  quality?: number;
  /**
   * The color painted behind transparent pixels
   */
  background?: string;
  /**
   * Bounding box, as [width, height] in pixels, the image is scaled down to
   * fit in. It is rotated to match the image orientation and never upscales
   */
  maxSize?: [number, number];
}

export interface FlattenedImage {
  blob: Blob;
  width: number;
  height: number;
}

/**
 * Re-encode an image over an opaque background, e.g. to turn a transparent
 * PNG into a JPEG without the transparent areas turning black, optionally
 * scaling it down to fit a `maxSize`.
 */
export const flattenImage = async (
  image: Blob,
  { type, quality, background = '#ffffff', maxSize }: FlattenImageOptions
): Promise<FlattenedImage> => {
  const bitmap = await createImageBitmap(image);
  const [width, height] = maxSize
    ? fitWithin(bitmap.width, bitmap.height, maxSize)
    : [bitmap.width, bitmap.height];

  const canvas = new OffscreenCanvas(width, height);
  const context = canvas.getContext('2d');
  if (!context) {
    bitmap.close();
    throw new Error('2D context not available');
  }

  context.fillStyle = background;
  context.fillRect(0, 0, width, height);
  context.imageSmoothingQuality = 'high';
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await canvas.convertToBlob({ type, quality });

  return { blob, width, height };
};

export default flattenImage;
